import { ExternalLink, Link2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { MarketplaceLogo } from './MarketplaceLogo';
import { Button } from './ui/button';

import { SUPPORTED_MARKETPLACES } from '../config/marketplaces';

const MARKETPLACES = SUPPORTED_MARKETPLACES.filter((m) => m.id !== 'other');

const AMAZON_TLDS = [
  'amazon.com', 'amazon.co.uk', 'amazon.ca', 'amazon.de',
  'amazon.it', 'amazon.co.jp', 'amazon.fr', 'amazon.es',
  'amazon.com.au', 'amazon.in', 'amazon.com.mx', 'amazon.com.br',
];

const AMAZON_AFFILIATE_TAG = process.env.REACT_APP_AMAZON_AFFILIATE_TAG || null;

export function buildAmazonAffiliateUrl(inputUrl) {
  if (!AMAZON_AFFILIATE_TAG || !inputUrl) {
    return inputUrl;
  }

  try {
    const url = new URL(inputUrl);
    const hostname = url.hostname.toLowerCase();
    const isAmazon = AMAZON_TLDS.some(
      (tld) => hostname === tld || hostname.endsWith('.' + tld)
    );
    if (!isAmazon || url.searchParams.has('tag')) {
      return inputUrl;
    }
    url.searchParams.set('tag', AMAZON_AFFILIATE_TAG);
    return url.toString();
  } catch {
    return inputUrl;
  }
}

function PasteHint({ pasteTarget = 'below' }) {
  if (pasteTarget === 'submit-page') {
    return (
      <p className="text-xs text-slate-500 mt-3">
        Browse a marketplace, copy the product link, then paste it on our Submit Product Link page to get your all-inclusive delivered price.
      </p>
    );
  }

  return (
    <p className="text-xs text-slate-500 mt-3">
      Browse a marketplace, copy the product link, then paste it in the box below to get your all-inclusive delivered price.
    </p>
  );
}

export default function MarketplaceAccessPanel({
  variant = 'default',
  className = '',
  pasteTarget = 'below',
  showSubmitCta = false,
  onSubmitClick,
}) {
  const navigate = useNavigate();

  const getMarketplaceUrl = (marketplace) => {
    if (marketplace.id === 'amazon') {
      return buildAmazonAffiliateUrl(marketplace.url);
    }
    return marketplace.url;
  };

  const handleSubmitClick = () => {
    if (onSubmitClick) {
      onSubmitClick();
      return;
    }
    navigate('/submit');
  };

  const submitButton = showSubmitCta ? (
    <Button
      type="button"
      onClick={handleSubmitClick}
      className="mt-4 w-full bg-gradient-to-r from-orange-500 via-amber-500 to-orange-600 hover:from-orange-400 hover:via-amber-400 hover:to-orange-500 text-white shadow-lg shadow-orange-500/25 font-semibold"
      data-testid="marketplace-submit-link-cta"
    >
      <Link2 className="h-4 w-4 mr-2" />
      Submit Product Link
    </Button>
  ) : null;

  if (variant === 'compact') {
    return (
      <div className={`bg-slate-50 border border-slate-200 rounded-lg p-4 ${className}`}>
        <p className="text-sm font-medium text-slate-700 mb-3">
          Find your product on these marketplaces:
        </p>
        <div className="flex flex-wrap gap-2">
          {MARKETPLACES.map((mp) => (
            <a
              key={mp.id}
              href={getMarketplaceUrl(mp)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 rounded-full text-sm text-slate-700 hover:border-orange-400 hover:text-orange-600 transition-colors"
              data-testid={`marketplace-link-${mp.id}`}
            >
              <MarketplaceLogo marketplace={mp.id} size="xs" />
              <span>{mp.name}</span>
              <ExternalLink className="h-3 w-3 opacity-50" />
            </a>
          ))}
        </div>
        <PasteHint pasteTarget={pasteTarget} />
        {submitButton}
        <p className="text-[10px] text-slate-600 mt-2">
          CheepSweepz is not affiliated with or endorsed by these marketplaces.
        </p>
      </div>
    );
  }

  return (
    <div className={`bg-white border border-slate-200 rounded-lg p-6 ${className}`}>
      <h3 className="text-lg font-semibold text-slate-900 mb-4">
        Find your product on these marketplaces
      </h3>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        {MARKETPLACES.map((mp) => (
          <a
            key={mp.id}
            href={getMarketplaceUrl(mp)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col items-center gap-2 p-3 bg-slate-50 border border-slate-200 rounded-lg hover:border-orange-400 hover:bg-orange-50 transition-all group"
            data-testid={`marketplace-link-${mp.id}`}
          >
            <MarketplaceLogo marketplace={mp.id} size="md" />
            <span className="text-xs font-medium text-slate-600 group-hover:text-orange-600">
              {mp.name}
            </span>
            <ExternalLink className="h-3 w-3 text-slate-400 group-hover:text-orange-500" />
          </a>
        ))}
      </div>

      <PasteHint pasteTarget={pasteTarget} />
      {submitButton}

      <p className="text-xs text-slate-600 mt-2">
        CheepSweepz is not affiliated with or endorsed by these marketplaces.
      </p>
    </div>
  );
}
